import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Contractor } from '../services/api';
import './ContractPicker.css';

export interface ContractorPickerProps {
  contractors: Contractor[];
  value: number | null;
  onChange: (id: number | null) => void;
  disabled?: boolean;
  placeholder?: string;
  emptyLabel?: string;
  allowEmpty?: boolean;
  className?: string;
  ariaLabel?: string;
  required?: boolean;
}

type PopupPosition = {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
};

const POPUP_MAX_HEIGHT = 320;

function contractorSearchText(contractor: Contractor): string {
  return [contractor.name, contractor.inn, contractor.contractor_type].filter(Boolean).join(' ').toLowerCase();
}

export const ContractorPicker: React.FC<ContractorPickerProps> = ({
  contractors,
  value,
  onChange,
  disabled,
  placeholder = 'Введите название или ИНН контрагента',
  emptyLabel = 'Выберите контрагента',
  allowEmpty = true,
  className,
  ariaLabel,
  required,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [popupPosition, setPopupPosition] = useState<PopupPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const sortedContractors = useMemo(
    () => [...contractors].sort((a, b) => a.name.localeCompare(b.name, 'ru')),
    [contractors],
  );

  const selected = useMemo(
    () => sortedContractors.find((contractor) => contractor.id === value) ?? null,
    [sortedContractors, value],
  );

  const selectedLabel = selected?.name ?? '';

  const filteredContractors = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedContractors;
    return sortedContractors.filter((contractor) => contractorSearchText(contractor).includes(q));
  }, [sortedContractors, query]);

  const updatePopupPosition = useCallback((): void => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const openUp = spaceBelow < 180 && spaceAbove > spaceBelow;
    const maxHeight = Math.min(POPUP_MAX_HEIGHT, Math.max(120, openUp ? spaceAbove : spaceBelow));

    setPopupPosition({
      left: rect.left,
      width: rect.width,
      maxHeight,
      ...(openUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePopupPosition();
    const onScrollOrResize = (): void => updatePopupPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updatePopupPosition]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (popupRef.current?.contains(target)) return;
      setOpen(false);
      setQuery('');
      setPopupPosition(null);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
        setPopupPosition(null);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const closePicker = (): void => {
    setOpen(false);
    setQuery('');
    setPopupPosition(null);
  };

  const openPicker = (): void => {
    if (disabled) return;
    setQuery('');
    setOpen(true);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const popup =
    open && popupPosition
      ? createPortal(
          <div
            ref={popupRef}
            className="contract-picker__popup contract-picker__popup--portal"
            role="listbox"
            aria-label={ariaLabel}
            style={{
              position: 'fixed',
              left: popupPosition.left,
              width: popupPosition.width,
              maxHeight: popupPosition.maxHeight,
              top: popupPosition.top,
              bottom: popupPosition.bottom,
            }}
          >
            {allowEmpty ? (
              <button
                type="button"
                role="option"
                aria-selected={value == null}
                className={`contract-picker__option${value == null ? ' contract-picker__option--active' : ''}`}
                onClick={() => {
                  onChange(null);
                  closePicker();
                }}
              >
                <span className="contract-picker__option-num">{emptyLabel}</span>
              </button>
            ) : null}
            {filteredContractors.length === 0 ? (
              <div className="contract-picker__empty">Ничего не найдено</div>
            ) : (
              filteredContractors.map((contractor) => {
                const isActive = contractor.id === value;
                return (
                  <button
                    type="button"
                    key={contractor.id}
                    role="option"
                    aria-selected={isActive}
                    className={`contract-picker__option${isActive ? ' contract-picker__option--active' : ''}`}
                    onClick={() => {
                      onChange(contractor.id);
                      closePicker();
                    }}
                  >
                    <span className="contract-picker__option-num">{contractor.name}</span>
                    {contractor.inn ? (
                      <span className="contract-picker__option-meta">ИНН {contractor.inn}</span>
                    ) : contractor.contractor_type ? (
                      <span className="contract-picker__option-meta">{contractor.contractor_type}</span>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      className={`contract-picker${disabled ? ' contract-picker--disabled' : ''}${className ? ` ${className}` : ''}`}
      ref={containerRef}
    >
      {required ? (
        <input
          className="contract-picker__validator"
          tabIndex={-1}
          value={value == null ? '' : String(value)}
          onChange={() => {}}
          required
          aria-hidden="true"
        />
      ) : null}
      {open ? (
        <input
          ref={inputRef}
          type="search"
          className="contract-picker__field"
          placeholder={selectedLabel || placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled}
          aria-label={ariaLabel ?? 'Поиск контрагента'}
          autoComplete="off"
        />
      ) : (
        <button
          type="button"
          className="contract-picker__trigger"
          onClick={openPicker}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className="contract-picker__text">
            {selectedLabel || (allowEmpty ? emptyLabel : placeholder)}
          </span>
          <span className="contract-picker__caret" aria-hidden>
            ▾
          </span>
        </button>
      )}
      {popup}
    </div>
  );
};

export default ContractorPicker;
